/*
    A collection of helper utilities for assignments.
*/
import {t} from "@lingui/core/macro";
import moment from "moment";

import {
    containsWritingCoachContent,
    defaultDueDate,
    getDay,
    isPastDate,
} from "@khan/assignments";
import type {ContentNodePath} from "@khan/content-types";
import {InitialRequestUrl} from "@khan/shared-core";
import {parseUrl, toRelativeUrl} from "@khan/urls";

import type {GqlAssignment} from "#coach-report-package/assignment-query.tsx";

import CreateAssignmentNotification from "./components/create-assignment-notification.tsx";
import UpdateAssignmentNotification from "./components/update-assignment-notification.tsx";
import {
    AssignmentStatus,
    type Assignment,
    type AssignmentStatusValues,
    type CompletionState,
    type CompletionStateEnum,
    type LearnableContent,
    type StudentList,
    type TopicPath,
    type TopicPathSegment,
    type UserHasChildAssignmentClassrooms,
} from "./types.ts";

export {
    DEFAULT_TIME,
    defaultDueDate,
    emptyAssignment,
    getDay,
    getMinDate,
    getValidDueDate,
    isPastDate,
    updateTime,
    getDefaultCourseMasteryDueDate,
    getExerciseId,
    getInitialAssignmentDueDate,
} from "@khan/assignments";

export {
    WRITING_COACH_CONTENT_DESCRIPTOR,
    containsWritingCoachContent,
} from "@khan/assignments";

export const AI_GUIDE_ACTIVITY_CONTENT_KIND = "AIGuideActivity";

export const WRITING_COACH_CONTENT_KIND = "AIGuideActivity";

export const WRITING_COACH_CONTENT_ID = "x1e6d165d54a37115";

// The date we started storing exercise task IDs on assignments.
// We only track the number of attempts for exercises assigned after this date.
export const ASSIGNMENT_TASK_TRACKING_START_DATE = "2017-08-24T17:00:00.000Z";

// sync-start:assignment-completion-states 500778268 https://github.com/Khan/webapp/blob/master/services/assignments/content_assignments/student_assignment_data.go
export const COMPLETION_STATES = {
    UNSTARTED: "unstarted",
    IN_PROGRESS: "in_progress",
    COMPLETED: "completed",
    LATE: "late",
    MISSED: "missed",
} as const;
// sync-end:assignment-completion-states

export const getCurrentMinute = () => {
    // Round down to the nearest minute
    return moment().seconds(0).milliseconds(0).toDate();
};

export const getValidationError = () => ({
    PAST_START_DATE: t`This date has passed.` as string,
    PAST_DUE_DATE: t`This date has passed.` as string,
    START_AFTER_DUE: t`Start date must be before due date` as string,
    DUE_BEFORE_START: t`Due date must be after start date` as string,
    DUE_AFTER_MAX:
        t`Due date must be before the maximum allowed date` as string,
});

export type ValidationErrorCode = keyof ReturnType<typeof getValidationError>;
export type ValidationErrorMessage = ReturnType<
    typeof getValidationError
>[keyof ReturnType<typeof getValidationError>];

export function dateOffsetFromDefaultDueDate(date: Date): number {
    return moment(date).diff(moment(defaultDueDate()), "days");
}

export function updateDay(dueDate: Date, newDate: Date): Date {
    const newDay = moment(newDate).startOf("day");

    return moment(
        `${newDay.format("DD/MM/YYYY")} ${getTime(dueDate)}`,
        "DD/MM/YYYY h:mm a",
    ).toDate();
}

export function getMaxDueDateWritingCoach() {
    // sync-start:wc-max-due-date 323689507 ./apps/writing-coach/src/utils/field-validators.ts
    return moment().add(90, "days").endOf("day");
    // sync-end:wc-max-due-date
}

/**
 * Returns only the date using a given format
 *
 * @param {Date} date The current date
 * @param {string} format The format used to return the date
 * @returns The formatted date
 *
 * @example getDate(new Date()) = "July 14"
 */
export function getDate(date: Date, format = "MMMM D"): string {
    return moment(date).format(format);
}

/**
 * Returns the time portion of a given date
 * @param {Date} date The current date
 * @returns The formatted time
 *
 * @example getDate(new Date()) = "9:30 AM"
 */
export function getTime(date: Date): string {
    return moment(date).format("h:mm A");
}

/**
 * Verifies if the selected assignments are valid to be updated using the bulk
 * edit mode.
 *
 * @param {?Date} startDate The start date (it can be null).
 * @param {?Date} dueDate The due date (it can be null).
 * @param {AssignmentStatus} assignmentsStatus The status of the assignments
 * (ASSIGNED, DRAFT, SCHEDULED).
 * @param {Date} [maxStartDate] The maximum start date to be used as starting
 * point. This means that the start date must be lower than this date.
 * @param {Date} [minDueDate] The minimum due date to be used as starting point.
 * This means that the due date must be higher than this date.
 * @param {Date} [maxDueDate] The maximum due date to be used.
 * This means that the due date must be lower than this date.
 *
 * @returns {Object} Object with info about whether the selected assignments are
 * valid to be updated.
 */
export function isValidToUpdateAssignments(
    startDate: Date | null | undefined,
    dueDate: Date | null | undefined,
    assignmentsStatus: AssignmentStatusValues,
    maxStartDate: Date = new Date(),
    minDueDate: Date = new Date(),
    maxDueDate?: Date,
): {
    isValid: boolean;
    startDateMessage?: string;
    dueDateMessage?: string;
} {
    const isNotAssignedYet = assignmentsStatus !== AssignmentStatus.ASSIGNED;

    // NOTE: We allow leaving the start date empty if the selected assignments
    // are "ASSIGNED" because we disable these controls from the UI.
    if (isNotAssignedYet && !startDate && !dueDate) {
        return {
            isValid: false,
        };
    }

    let startDateMessage;
    let dueDateMessage;
    const ValidationError = getValidationError();

    // The start date should be set in the future.
    if (isNotAssignedYet && startDate && isPastDate(startDate)) {
        startDateMessage = ValidationError.PAST_START_DATE;
    }

    // Verify that the due date is set after the start date (if set).
    if (startDate && dueDate && moment(dueDate).isSameOrBefore(startDate)) {
        dueDateMessage = ValidationError.DUE_BEFORE_START;
    }

    // Verify that the start date is set before the minimum start date that is
    // calculated based on all the selected assignments.
    // NOTE: This rule only applies for non-assigned assignments.
    if (
        isNotAssignedYet &&
        startDate &&
        moment(startDate).isSameOrAfter(maxStartDate)
    ) {
        startDateMessage = ValidationError.START_AFTER_DUE;
    }

    // Verify that the due date is set after the minimum due date that is
    // calculated based on all the selected assignments.
    if (dueDate && moment(dueDate).isSameOrBefore(minDueDate)) {
        dueDateMessage = ValidationError.DUE_BEFORE_START;
    }

    // Verify that due date is not in the past.
    if (dueDate && dueDate.getTime() < new Date().getTime()) {
        dueDateMessage = ValidationError.PAST_DUE_DATE;
    }

    if (dueDate && maxDueDate && moment(dueDate).isAfter(maxDueDate)) {
        dueDateMessage = ValidationError.DUE_AFTER_MAX;
    }

    if (startDateMessage || dueDateMessage) {
        return {
            isValid: false,
            startDateMessage,
            dueDateMessage,
        };
    }

    return {
        isValid: true,
    };
}

/**
 * Verifies if the current assignment is valid to be posted to students.
 */
export function isValidToPost(
    assignment: Assignment,
    classDescriptors?: Array<string>,
): boolean {
    if (!isValidToDraft(assignment, classDescriptors)) {
        return false;
    }

    const assignmentHasStudents =
        assignment.students && assignment.students.length;
    const studentList = assignment.studentList;
    const studentListHasNoStudents =
        studentList &&
        (studentList.countStudents === 0 ||
            studentList.studentKaidsAndNicknames?.length === 0);
    // We allow posting zero-assignee assignments only when the class has no students
    const validForSingle = assignmentHasStudents || studentListHasNoStudents;
    const validForMulti = classDescriptors && classDescriptors.length > 1;

    if (!validForMulti && !validForSingle) {
        return false;
    }

    if (!assignment.dueDate) {
        return false;
    }

    return true;
}

/**
 * Verifies if the current assignment contains a valid start date.
 *
 * @param {Assignment} assignment The assignment to be checked.
 *
 * @returns {?ValidationErrorMessage} If the date is invalid, it returns the
 *  error message. Otherwise, it returns null.
 */
export function validateStartDate(
    assignment: Assignment,
): ValidationErrorMessage | null {
    const {assignmentStatus, startDate} = assignment;

    if (
        assignmentStatus !== AssignmentStatus.ASSIGNED &&
        isPastDate(startDate)
    ) {
        return getValidationError().PAST_START_DATE;
    }

    return null;
}

/**
 * Verifies if the current assignment contains a valid due date.
 *
 * @param {Assignment} assignment The assignment to be checked.
 *
 * @returns {?ValidationErrorMessage} If the date is invalid, it returns the
 *  error message. Otherwise, it returns null.
 */
export function validateDueDate(
    assignment: Assignment,
    maxDueDate?: Date,
): ValidationErrorMessage | null {
    const {dueDate, startDate} = assignment;
    const ValidationError = getValidationError();

    // Verify that the due date is not set in the past.
    if (isBeforeNow(dueDate)) {
        return ValidationError.PAST_DUE_DATE;
    }

    // Verify that the due date is set after the start date.
    if (moment(dueDate).isSameOrBefore(startDate)) {
        return ValidationError.DUE_BEFORE_START;
    }

    if (maxDueDate && moment(dueDate).isAfter(maxDueDate)) {
        return ValidationError.DUE_AFTER_MAX;
    }

    return null;
}

/**
 * Verifies if the current assignment is valid to be created/updated as a draft.
 */
export function isValidToDraft(
    assignment: Assignment,
    classDescriptors?: Array<string>,
): boolean {
    const validForMulti = classDescriptors && classDescriptors.length > 1;
    const validForSingle = assignment.studentList && assignment.studentList.id;

    if (!validForMulti && !validForSingle) {
        return false;
    }

    // The start date should be set in the future.
    // NOTE: This rule applies only for drafts or scheduled assignments that are
    // being created.
    if (validateStartDate(assignment)) {
        return false;
    }

    if (validateDueDate(assignment)) {
        return false;
    }

    return true;
}

export function isToday(date: Date): boolean {
    return date && moment(getDay(date)).isSame(new Date(), "day");
}

export function isBeforeNow(date: Date): boolean {
    return moment(date).isBefore();
}

export function isAfterNow(date: Date): boolean {
    return moment(date).isAfter();
}

export function formatDate(
    date: Date | string,
    showDateOnly?: boolean,
    showRecentTimeSimply?: boolean,
): string {
    const dateFormat = {
        lastDay: t`[Yesterday]`,
        sameDay: t`[Today]`,
        nextDay: t`[Tomorrow]`,
        lastWeek: showRecentTimeSimply ? "dddd" : "MMM Do",
        nextWeek: showRecentTimeSimply ? "dddd" : "MMM Do",
        sameElse: "MMM Do",
    } as const;

    const timeFormat = "h:mm A";

    const formattedDate = moment(date).calendar(null, dateFormat);
    if (showDateOnly) {
        return formattedDate;
    }
    const formattedTime = moment(date).format(timeFormat);
    return `${formattedDate}, ${formattedTime}`;
}

/**
 * The format that will be used in the exported CSV
 *
 * NOTE: Exported only for testing
 */
export const CSV_DATE_FORMAT = "MMM Do, h:mmA";

/**
 * Converts a date given a specific format
 *
 * @param {?Date} date - The date that will be formatted
 * @returns {string}
 */
export const formatDateForCsv = (
    date?: Date | null,
    format: string = CSV_DATE_FORMAT,
): string => moment(date).format(format);

/**
 * NOTE(slim): Defines whether a report is visible for an individual student.
 * This is more of a product decision, but we have a separate predicate
 * to enforce uniform behavior across components when deciding whether to
 * render links to reports.
 */
export const isStudentReportVisible = (state: CompletionStateEnum): boolean =>
    state === COMPLETION_STATES.COMPLETED || state === COMPLETION_STATES.LATE;

export function getTotalStudentCount(
    studentLists: Array<StudentList>,
    selectedClassDescriptors: Array<string>,
): number {
    const selectedStudentLists = studentLists.filter((list) =>
        selectedClassDescriptors.includes(list.descriptor),
    );
    return selectedStudentLists.reduce((acc, list) => {
        const totalStudentCount =
            list.countStudents ||
            (list.students && list.students.length) ||
            (list.studentKaidsAndNicknames &&
                list.studentKaidsAndNicknames.length) ||
            0;
        return acc + totalStudentCount;
    }, 0);
}

export const addQueryParamsToRelativeUrl = (
    relativeUrl: string,
    queryParams: Array<{
        key: string;
        value: string;
    }>,
): string => {
    const url = parseUrl(relativeUrl);

    // Add new query param
    for (const queryParam of queryParams) {
        url.searchParams.set(queryParam.key, queryParam.value);
    }

    return toRelativeUrl(url.href);
};

export function getContent(assignment: Readonly<Assignment>): LearnableContent {
    return assignment.contents && assignment.contents.length
        ? assignment.contents[0]
        : {
              title: t`This assignment is not available in your language.`,
              kind: "",
              duration: 0,
              defaultUrlPath: "/",
              topicPaths: [],
          };
}

// The backend uses `topic_paths[n]` to construct the `specificUrlPath`. This URL
// is what is used in modals for the "Go to lesson" link. Therefore, for LTv2
// purposes we can attempt to extract domain/course/unit/lesson (DCUL) information
// from `topicPaths[n]` for student assignments.
export const getContentPath = (
    topicPaths?: Array<TopicPath> | null,
    contentId?: string | null,
    subjectSlug?: string | null,
): ContentNodePath | null | undefined => {
    if (
        !topicPaths ||
        topicPaths.length < 1 ||
        !contentId ||
        contentId.length < 1
    ) {
        return null;
    }

    const contentIndex = getContentIndex(topicPaths, subjectSlug);
    const contentPath = topicPaths[contentIndex].path;

    // We expect topic paths to be 5 levels deep: Domain -> Course -> Unit -> Lesson -> Content
    const EXPECTED_TOPIC_PATH_DEPTH = 5;
    // Content should be the last entry in the topicPath
    const TOPIC_PATH_CONTENT_INDEX = EXPECTED_TOPIC_PATH_DEPTH - 1;

    // On occasion topicPaths are longer, just deal with "standard" paths for now.
    // ex. curation courses like https://www.khanacademy.org/math/high-school-math/math2/xe2ae2386aa2e13d6:quad-1

    if (
        contentPath &&
        contentPath.length === EXPECTED_TOPIC_PATH_DEPTH &&
        contentPath[TOPIC_PATH_CONTENT_INDEX].id === contentId
    ) {
        return {
            domainId: contentPath[0].id,
            courseId: contentPath[1].id,
            unitId: contentPath[2].id,
            lessonId: contentPath[3].id,
            lessonSlug: contentPath[3].slug,
            specificUrlPath: createSpecificUrlPath(contentPath),
        };
    }
    return null;
};

// The backend uses the `topic_paths[n].path` array to construct the `specificUrlPath`. This URL
// is what is used in modals for the "Go to lesson" link. Therefore, for LTv2
// purposes we can attempt to extract domain/course/unit/lesson/contentType/content (DCUL) information.
const createSpecificUrlPath = (
    contentPath: Array<TopicPathSegment>,
): string | null | undefined => {
    // We expect topic paths to be 5 levels deep: Domain -> Course -> Unit -> Lesson -> Content
    const EXPECTED_TOPIC_PATH_DEPTH = 5;
    if (contentPath.length !== EXPECTED_TOPIC_PATH_DEPTH) {
        return null;
    }
    const kindUrlMap = {
        Video: "v",
        Exercise: "e",
        Article: "a",
        Talkthrough: "pt",
        Challenge: "pc",
        Interactive: "pi",
        Project: "pp",
    } as const;
    return (
        "/" +
        [
            contentPath[0].slug,
            contentPath[1].slug,
            contentPath[2].slug,
            contentPath[3].slug,
            kindUrlMap[contentPath[4].kind],
            contentPath[4].slug,
        ].join("/")
    );
};

const getContentIndex = (
    topicPaths: Array<TopicPath>,
    subjectSlug?: string | null,
): number => {
    const contentIndex = topicPaths.findIndex(
        (topicPath) =>
            topicPath.path &&
            topicPath.path.length > 0 &&
            // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
            topicPath.path.map((segment) => segment.slug).includes(subjectSlug),
    );
    return contentIndex !== -1 ? contentIndex : 0;
};

export const getActivityAssignmentUrl = (assignment: Assignment): string => {
    const content = getContent(assignment);
    let href = content.defaultUrlPath;
    if (content.kind === "AIGuideActivity" && assignment.id) {
        // Include the assignment ID for Khanmigo Assignments
        // since the assignment may be customized from the default content.
        href = addQueryParamsToRelativeUrl(href, [
            {
                key: "assignment",
                value: assignment.id,
            },
        ]);
    }
    return href;
};

export const getWritingCoachReportUrl = (
    assignmentId: string,
    studentKaid?: string,
): string => {
    const absoluteUrl = (path: string) => `${InitialRequestUrl.origin}${path}`;
    let reportUrl = `/teacher/report/assignment/${assignmentId}`;
    reportUrl = studentKaid
        ? addQueryParamsToRelativeUrl(reportUrl, [
              {
                  key: "kaid",
                  value: studentKaid,
              },
          ])
        : reportUrl;

    return absoluteUrl(reportUrl);
};

export const getScore = (studentCompletion: CompletionState): null | number => {
    // Guard against legacy, incomplete, or non-exercise assignments
    if (
        !studentCompletion ||
        !studentCompletion.bestScore ||
        typeof studentCompletion.bestScore.numAttempted !== "number"
    ) {
        return null;
    }

    const {bestScore} = studentCompletion;

    return Math.round((bestScore.numCorrect / bestScore.numAttempted) * 100);
};

export const getContentTitle = (assignment?: Assignment | null): string => {
    return (
        (assignment &&
            assignment.contents &&
            assignment.contents[0] &&
            assignment.contents[0].title) ||
        ""
    );
};

export const getUniqueCount = (items: Array<string>): number => {
    const uniqueItemMap = items.reduce<Record<string, any>>(
        (idMap: Record<string, boolean>, item) => {
            idMap[item] = true;
            return idMap;
        },
        {},
    );
    return Object.keys(uniqueItemMap).length;
};

export const triggerCreateAssignmentNotification = (
    assignments: Array<Assignment>,
    createAction: "ASSIGN" | "SAVE",
    requestedContentTitle: string,
    requestedClassCount = 1,
    requestedContentCount = 1,
) => {
    const successContentCount = getUniqueCount(
        assignments.map(
            (a) => (a.contents && a.contents[0] && a.contents[0].id) || "",
        ),
    );
    const successClassCount = getUniqueCount(
        assignments.map((a) => (a.studentList && a.studentList.id) || ""),
    );
    const studentList = assignments[0] && assignments[0].studentList;
    const studentCount = assignments[0] && assignments[0].students.length;
    const contentTitle =
        requestedContentTitle || getContentTitle(assignments[0]);
    const assignedToChildAssignmentsClassroom =
        getAssignedToChildAssignmentsClassrooms(assignments);

    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <CreateAssignmentNotification
                createAction={createAction}
                successContentCount={successContentCount}
                successClassCount={successClassCount}
                requestedClassCount={requestedClassCount}
                requestedContentCount={requestedContentCount}
                // eslint-disable-next-line @khanacademy/ts-no-error-suppressions
                // @ts-expect-error - TS2322 - name should not be nullable
                studentList={studentList ?? undefined}
                contentTitle={contentTitle}
                studentCount={studentCount}
                assignedToChildAssignmentsClassroom={
                    assignedToChildAssignmentsClassroom
                }
            />,
        );
    });
};

export const triggerDuplicateErrorToast = () => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(<CreateAssignmentNotification isDuplicateError={true} />);
    });
};

export const triggerUpdateAssignmentNotification = (assignment: Assignment) => {
    const contentTitle = getContentTitle(assignment);
    const studentListName =
        (assignment.studentList && assignment.studentList.name) || "";

    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                studentListName={studentListName}
                isChildAssignmentsClassroom={
                    !!assignment.studentList?.isChildAssignmentsClassroom
                }
            />,
        );
    });
};

export const triggerUpdateAssignmentsNotification = (
    contentTitle: string,
    studentListName = "",
    isChildAssignmentsClassroom: boolean,
) => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                studentListName={studentListName}
                isChildAssignmentsClassroom={isChildAssignmentsClassroom}
            />,
        );
    });
};

export const triggerUpdateAssignmentErrorNotification = (
    contentTitle: string,
) => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                isError={true}
                isChildAssignmentsClassroom={false}
            />,
        );
    });
};

export const isExerciseContent = (
    contentDescriptors?: Array<string> | null,
): boolean => {
    if (!contentDescriptors || contentDescriptors.length === 0) {
        return false;
    }
    for (const contentDescriptor of contentDescriptors) {
        if (contentDescriptor.startsWith("Exercise")) {
            return true;
        }
    }
    return false;
};

export const isWritingCoachAssignment = (
    assignment?: Assignment | GqlAssignment | null,
): boolean => {
    if (!assignment?.contentDescriptors?.length) {
        return false;
    }

    return containsWritingCoachContent(assignment.contentDescriptors);
};

export const isAIGuideActivityContentKind = (
    contentKind: string | null | undefined,
): boolean => {
    return contentKind === AI_GUIDE_ACTIVITY_CONTENT_KIND;
};

export const isWritingCoachContent = (
    contentId: string | null | undefined,
    contentKind: string | null | undefined,
): boolean => {
    return (
        contentId === WRITING_COACH_CONTENT_ID &&
        contentKind === WRITING_COACH_CONTENT_KIND
    );
};

export const containsExerciseContent = (
    qualifiedContentDescriptorsList: Array<string>,
): boolean => {
    if (qualifiedContentDescriptorsList.length === 0) {
        return false;
    }
    for (const contentDescriptor of qualifiedContentDescriptorsList) {
        if (contentDescriptor.includes("Exercise")) {
            return true;
        }
    }
    return false;
};

export const containsExerciseContentOnly = (
    qualifiedContentDescriptorsList: Array<string>,
): boolean => {
    if (qualifiedContentDescriptorsList.length === 0) {
        return false;
    }
    for (const contentDescriptor of qualifiedContentDescriptorsList) {
        if (!contentDescriptor.includes("Exercise")) {
            return false;
        }
    }
    return true;
};

export const containsAIGuideActivityContent = (
    contentDescriptorsList: Array<string>,
): boolean => {
    if (contentDescriptorsList.length === 0) {
        return false;
    }
    for (const contentDescriptor of contentDescriptorsList) {
        if (contentDescriptor.includes(AI_GUIDE_ACTIVITY_CONTENT_KIND)) {
            return true;
        }
    }
    return false;
};

const getAssignedToChildAssignmentsClassrooms = (
    assignments: Array<Assignment>,
): UserHasChildAssignmentClassrooms => {
    let childAssignmentClassroomCount = 0;

    for (const assignment of assignments) {
        if (assignment.studentList?.isChildAssignmentsClassroom) {
            childAssignmentClassroomCount++;
        }
    }

    if (childAssignmentClassroomCount === assignments.length) {
        return "ALL";
    }

    if (childAssignmentClassroomCount > 1) {
        return "SOME";
    }

    return "NONE";
};

export const getUserHasChildAssignmentsClassroomStatus = (
    studentLists: StudentList[],
): UserHasChildAssignmentClassrooms => {
    let cacCount = 0;

    for (const studentList of studentLists) {
        if (studentList.isChildAssignmentsClassroom) {
            cacCount++;
        }
    }

    if (cacCount === studentLists.length) {
        return "ALL";
    }

    if (cacCount > 0) {
        return "SOME";
    }

    return "NONE";
};
